import cn from "classnames"

import { useDocumentationToken } from "@/api/useAuth"
import { Button, Dropdown, Tooltip } from "@/atoms"
import { EXTERNAL_ROUTES } from "@/const/routes.constants"
import { getColorNumberForUserAvatar } from "@/domain/user"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { ReadOutlined, SettingOutlined } from "@/icons"
import { KaiButton } from "@/molecules/KaiButton"
import { useKeyByParams } from "../SideNavigation/hooks/useKeyByParams"
import { LanguageItem } from "./LanguageItem"
import { getNavMenu } from "./NavMenu"
import { getUserMenu } from "./UserMenu"

import styles from "./MenuHeader.module.scss"

export const MenuHeader = () => {
  const { user, signOut, hasPermission, changeLanguage } = useUser()
  const currentKey = useKeyByParams()
  const { t, i18n } = useTranslation()
  const { t: tSettings } = useTranslation("translation", {
    keyPrefix: "settings"
  })
  const multiLanguage = useFeatureFlags("multiLanguage")

  const { isActive, ...navMenu } = getNavMenu(currentKey, {
    t: tSettings,
    hasPermission
  })
  const { refetch, isRefetching } = useDocumentationToken({ enabled: false })
  const { logger, EVENTS } = useLogger()

  if (!user.email) {
    return <div className={cn(styles.container, styles.noUser)} />
  }

  const style = {
    backgroundColor: `var(--color-${getColorNumberForUserAvatar(user.email)}-avatar-palette)`,
    color: "var(--neutral-10-color)",
    fontSize: "14px"
  }

  const currentLanguage = (i18n.language as Settings.Language) || "en"

  return (
    <div className={styles.container}>
      {!isSelfHosted() && (
        <Tooltip title={tSettings("kodemKnowledge")}>
          <Button
            loading={isRefetching}
            className={styles.buttonMenu}
            icon={<ReadOutlined />}
            type="text"
            onClick={async () => {
              try {
                logger.info(EVENTS.ANALYTIC_EVENTS.OPEN_API_DOCUMENTATION)

                const { data: response } = await refetch()
                const token = response?.data?.token

                if (token)
                  window.open(
                    `${EXTERNAL_ROUTES.API_DOCS}?${EXTERNAL_ROUTES.PARAMS.JWT_TOKEN}=${token}`,
                    "_blank"
                  )

                if (!token)
                  logger.error("API Documentation - token is not available")
              } catch (err: any) {
                logger.error(err)
              }
            }}
          />
        </Tooltip>
      )}
      <Dropdown
        rootClassName={cn(styles.root, styles.navMenu)}
        menu={
          multiLanguage
            ? {
                items: [
                  {
                    key: "language-title",
                    disabled: true,
                    label: t("general.language")
                  },
                  {
                    type: "submenu",
                    popupClassName: styles.language,
                    label: (
                      <LanguageItem
                        lng={currentLanguage}
                        label={tSettings(`languages.${currentLanguage}`)}
                      />
                    ),
                    key: "language-submenu",
                    children: [
                      {
                        key: "en",
                        label: (
                          <LanguageItem
                            lng="en"
                            label={tSettings("languages.en")}
                          />
                        ),
                        onClick: () => {
                          changeLanguage("en")
                        }
                      },
                      {
                        key: "ch",
                        label: (
                          <LanguageItem
                            lng="ch"
                            label={tSettings("languages.ch")}
                          />
                        ),
                        onClick: () => {
                          changeLanguage("ch")
                        }
                      }
                    ]
                  },
                  ...navMenu.items
                ]
              }
            : navMenu
        }
      >
        <Button
          className={cn(styles.buttonMenu, isActive && styles.activeButtonMenu)}
          icon={<SettingOutlined />}
          type="text"
        />
      </Dropdown>
      <KaiButton />
      <div className={styles.userItemContainer}>
        <Dropdown rootClassName={styles.root} menu={getUserMenu(user, signOut)}>
          <span style={style} className={styles.userItem}>
            {user.initials}
          </span>
        </Dropdown>
      </div>
    </div>
  )
}
